import React, { useContext, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { UserContext } from '../Auth.jsx';
import { useDrawerContext } from '../../contexts/DrawerContext';
import useNavigation from '../../hooks/useNavigation';
import navigationItems from './navigationItems';
import MobileDrawer from './MobileDrawer';
import DesktopDrawer from './DesktopDrawer';
import ImpersonationIndicator from './ImpersonationIndicator';
import ImpersonationSelector from '../ImpersonationSelector.jsx';

/**
 * Sidebar component for application navigation
 * Handles both mobile and desktop layouts
 * 
 * @returns {JSX.Element} Sidebar component
 */
const Sidebar = () => {
  const { 
    user, 
    originalUser, 
    isImpersonating, 
    startImpersonation, 
    stopImpersonation,
    logout
  } = useContext(UserContext);
  
  const theme = useTheme();
  
  // Drawer state management
  const {
    isDesktop,
    mobileOpen,
    miniDrawer,
    handleDrawerOpen,
    handleDrawerClose,
    handleDrawerToggle,
    setMobileOpen
  } = useDrawerContext();
  
  // Navigation state management
  const {
    openCollapsible,
    handleToggleCollapsible,
    isItemActive,
    activeItem
  } = useNavigation(navigationItems);
  
  // Impersonation dialog state
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);
  
  // Determine if current session has admin privileges
  // Always check the current active user's admin status (whether original or impersonated)
  const hasAdminAccess = user?.admin;
  
  // Handle starting impersonation
  const handleStartImpersonation = () => {
    setImpersonationDialogOpen(true);
    // Don't close drawer - let the toggle button control drawer state
  };
  
  // Handle selecting a user to impersonate
  const handleSelectUser = (impersonatedUser) => {
    startImpersonation(impersonatedUser);
  };
  
  // Handle stopping impersonation
  const handleStopImpersonation = async () => {
    await stopImpersonation();
    // Don't close drawer - let the toggle button control drawer state
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Note: Mobile toggle is now handled by TopNavigationBar */}
      
      {/* Mobile Drawer */}
      {!isDesktop && (
        <MobileDrawer
          open={mobileOpen}
          onOpen={handleDrawerOpen}
          onClose={handleDrawerClose}
          navigationItems={navigationItems}
          openCollapsible={openCollapsible}
          onToggleCollapsible={handleToggleCollapsible}
          isImpersonating={isImpersonating}
          user={user}
          originalUser={originalUser}
          hasAdminAccess={hasAdminAccess}
          onStartImpersonation={handleStartImpersonation}
          onStopImpersonation={handleStopImpersonation}
          onLogout={handleLogout}
        />
      )}
      
      {/* Desktop Drawer */}
      {isDesktop && (
        <DesktopDrawer
          miniDrawer={miniDrawer}
          onDrawerOpen={handleDrawerOpen}
          onDrawerClose={handleDrawerClose}
          navigationItems={navigationItems}
          openCollapsible={openCollapsible}
          onToggleCollapsible={handleToggleCollapsible}
          isImpersonating={isImpersonating}
          user={user}
          originalUser={originalUser}
          hasAdminAccess={hasAdminAccess}
          onStartImpersonation={handleStartImpersonation}
          onStopImpersonation={handleStopImpersonation}
          onLogout={handleLogout}
        />
      )}
      
      {/* Impersonation Selector Dialog */}
      <ImpersonationSelector
        open={impersonationDialogOpen}
        onClose={() => setImpersonationDialogOpen(false)}
        onSelectUser={handleSelectUser}
      />
    </Box>
  );
};

export default Sidebar;
